import { Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useToast } from '../../../../Hooks/useToast';
import { useUser } from '../../../../Hooks/useUser';
import { useUpdateTalentMutation } from '../../../../Redux/api/talentApi';
import { useUpdateClientMutation } from '../../../../Redux/api/clientApi';
import { ProfileType } from '../../../../Types/General';
import { KaziModal } from '../../../../Shared Components/Kazi/KaziModal';
import { EditAccountUserInfo } from './EditAccountUserInfo';
import { EditAccountLocationInfo } from './EditAccountLocationInfo';

interface EditAccountModalForm {    
    userId: string;
    first_name: string,
    last_name: string,
    email: string;
    country: string;
    phone: string;
    // IdType: string;
    // IdNumber: string;
}

interface EditAccountModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

export const EditAccountModal = ( { open, setOpen }: EditAccountModalProps ) => {
    /* ******************** Hooks ******************** */
    const { showToast } = useToast();
    const { user, type } = useUser();
    const [updateTalent, {data: talentUpdateResponse, isLoading: talentUpdateLoading, isSuccess: talentUpdateSuccesful}] = useUpdateTalentMutation();
    const [updateClient, {data: clientUpdateResponse, isLoading: clientUpdateLoading, isSuccess: clientUpdateSuccesful}] = useUpdateClientMutation();
    
    const getDefaultValues = () => {
        return {
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
            country: user?.country,
            phone: user?.phone
        }
    }
    const {
        control,
        formState: {dirtyFields, isDirty},
        getValues,
        reset,
        handleSubmit
    } = useForm<EditAccountModalForm>({
        defaultValues: getDefaultValues()
    })
    /* ******************** Variables ******************** */
    const [step, setStep] = useState(1);
    /* ******************** Functions ******************** */
    const handleBack = () => {
        if (step == 1) handleClose();
        if (step == 2) setStep(step-1);
    }
    const handleNext = () => {
        if (step == 1) setStep(step+1);
        if (step == 2) handleSubmit(handleSave)();
    }
    const handleClose = () =>{
        setOpen(false);
        setStep(1)
    }
    const getUpdatedData = (data: EditAccountModalForm) => {
        const keys = Object.keys(dirtyFields);
        const values = Object.values(dirtyFields);
        let dirtyArray: string[][] = [];
        values.forEach((val, idx) => {
            const key = keys[idx];
            //@ts-ignore
            if (val) dirtyArray.push([key, data[keys[idx]]])
        })
        return Object.fromEntries(dirtyArray);
    }
    const handleSave = (data: EditAccountModalForm) =>{
        const updated_data = getUpdatedData(data);
        if (type ==  ProfileType.Talent) {
            updateTalent({
                locator: user?.locator,
                updated_data
            })
            .unwrap()
            .then(res => {
                handleClose()
                showToast({
                    text: 'Account updated successfully',
                    variant: 'success'
                })
            })
            .catch(err => {
                console.log(err);
                showToast({
                    text: "Something went wrong",
                    variant: 'error'
                })
            })
        }
        if (type ==  ProfileType.Client) {
            updateClient({
                locator: user?.locator,
                updated_data
            })
            .unwrap()
            .then(res => {
                handleClose()
                showToast({
                    text: 'Account updated successfully',
                    variant: 'success'
                })
            })
            .catch(err => {
                console.log(err);
                showToast({
                    text: "Something went wrong",
                    variant: 'error'
                })
            })
        }
    }
    const getContent = () => {
        switch(step) {
            case 1:
                return <EditAccountUserInfo control={control}/>;
            case 2: 
                return <EditAccountLocationInfo control={control} getValue={getValues} />;
        }
    }
    /* ******************** Effects ******************** */
    useEffect(() =>{
        reset(getDefaultValues())
    }, [user])
    /* ******************** JSX ******************** */
    return (
        <KaziModal 
            open={open}
            handleClose={handleClose}
            title='Edit Account Info'
            content={getContent()}
            actions={[
                <Button variant={'outlined'} onClick={handleBack}>{step == 1 ? 'Cancel' : 'Back'}</Button>,
                <Button 
                    variant={'contained'} 
                    onClick={handleNext}
                    disabled={step == 2 && !isDirty}
                    sx={{
                        "&.Mui-disabled": {
                            backgroundColor: 'black.50',
                            border: 'none',
                            color: 'primary.main'
                        }
                    }}
                >
                    {step == 1 ? 'Next' : 'Save'}
                </Button>
            ]}
            stepper={{
                current: step,
                total: 2
            }}
        />
    )
}